import type { MarkConfig } from '../types';
import { DEFAULT_ENDPOINT } from './constants';
import type { TransportAdapter } from './adapters';
import { TransportError, parseRetryAfter } from './TransportError';

export class HttpTransport implements TransportAdapter {
  private readonly config: MarkConfig;
  private readonly endpoint: string;
  private readonly pending = new Set<Promise<void>>();

  constructor(config: MarkConfig) {
    this.config = config;
    this.endpoint = config.endpoint ?? DEFAULT_ENDPOINT;
  }

  public async send(path: string, data: unknown, options?: { preferBeacon?: boolean }) {
    const request = this.sendInternal(path, data, options);
    this.pending.add(request);
    try {
      await request;
    } finally {
      this.pending.delete(request);
    }
  }

  public async flush() {
    if (this.pending.size === 0) {
      return;
    }
    await Promise.allSettled(Array.from(this.pending));
  }

  private async sendInternal(path: string, data: unknown, options?: { preferBeacon?: boolean }) {
    const url = this.joinUrl(this.endpoint, path);
    const key = this.config.key;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      [key.startsWith('sk_') ? 'x-secret-key' : 'x-publishable-key']: key,
    };

    if (this.config.debug) {
      console.log('[Mark] Sending', url, data);
    }

    const body = JSON.stringify(data);
    if (options?.preferBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      const sent = navigator.sendBeacon(url, blob);
      if (sent) {
        return;
      }
    }

    if (typeof fetch !== 'function') {
      if (this.config.debug) {
        console.error('[Mark] Global fetch is not available in this runtime.');
      }
      throw new TransportError('[Mark] Global fetch is not available in this runtime.');
    }

    const timeoutMs = this.config.request_timeout_ms ?? 10000;
    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body,
        keepalive: true,
        signal: controller.signal,
      });
      if (!response.ok) {
        const snippet = await this.readErrorSnippet(response);
        const retryAfterMs = parseRetryAfter(response.headers.get('Retry-After'));
        if (this.config.debug) {
          console.error('[Mark] Request rejected', {
            url,
            status: response.status,
            statusText: response.statusText,
            body: snippet,
            retryAfterMs,
          });
        }
        throw new TransportError(
          `[Mark] Request rejected with status ${response.status}: ${snippet}`,
          { status: response.status, retryAfterMs }
        );
      }
    } catch (error) {
      if (this.config.debug) {
        console.error('[Mark] Failed to send', url, error);
      }
      if (error instanceof TransportError) {
        throw error;
      }
      if (timedOut) {
        throw new TransportError(`[Mark] Request timed out after ${timeoutMs}ms`, { status: 408 });
      }
      const message = error instanceof Error ? error.message : String(error);
      throw new TransportError(`[Mark] Network error: ${message}`);
    } finally {
      clearTimeout(timer);
    }
  }

  private joinUrl(endpoint: string, path: string) {
    const normalizedEndpoint = endpoint.replace(/\/+$/, '');
    const normalizedPath = path.replace(/^\/+/, '');
    return `${normalizedEndpoint}/${normalizedPath}`;
  }

  private async readErrorSnippet(response: Response) {
    try {
      const text = await response.text();
      return text.slice(0, 300);
    } catch {
      return '';
    }
  }
}
